import {
  ReceiptLongOutlined,
  PaymentsOutlined,
  PercentOutlined,
  ScheduleOutlined,
  RecyclingOutlined,
  LocalShippingOutlined,
  PersonOutlined,
  RepeatOutlined,
  InsightsOutlined,
} from "@mui/icons-material";
import type { SvgIconComponent } from "@mui/icons-material";
import type { Theme } from "@mui/material/styles";

export type StatCategory =
  | "financial"
  | "sustainability"
  | "operations"
  | "performance"
  | "time";

/** Pick a representative icon + semantic category for a metric based on its title/unit. */
export const getStatVisual = (
  title: string,
  unit = ""
): { Icon: SvgIconComponent; category: StatCategory } => {
  const t = title.toUpperCase();

  if (unit === "SAR" || unit === "SAR/kg" || /REVENUE|INCENTIVE/.test(t)) {
    return { Icon: PaymentsOutlined, category: "financial" };
  }
  if (/ORDER/.test(t)) {
    return { Icon: ReceiptLongOutlined, category: "operations" };
  }
  if (unit === "hours" || /TIME/.test(t)) {
    return { Icon: ScheduleOutlined, category: "time" };
  }
  if (/TRIP|TRUCK/.test(t)) {
    return { Icon: LocalShippingOutlined, category: "operations" };
  }
  if (/DRIVER|SATISFACTION/.test(t)) {
    return { Icon: PersonOutlined, category: "performance" };
  }
  if (/FREQUENCY|EMERGENCY|FILL|REPORT/.test(t)) {
    return { Icon: RepeatOutlined, category: "operations" };
  }
  if (unit.includes("kg") || /WASTE|SUPPLY|SUSTAINABLE/.test(t)) {
    return { Icon: RecyclingOutlined, category: "sustainability" };
  }
  if (
    unit === "%" ||
    /RATE|PERCENT|SCORE|UTILIZATION|COMPLIANCE|EFFICIENCY/.test(t)
  ) {
    return { Icon: PercentOutlined, category: "performance" };
  }
  return { Icon: InsightsOutlined, category: "operations" };
};

/** Category → accent color (theme-aware, limited palette to avoid a "rainbow" look). */
export const CATEGORY_COLOR: Record<StatCategory, (theme: Theme) => string> = {
  financial: (theme) => theme.palette.success.main,
  sustainability: (theme) => theme.palette.secondary.main,
  operations: (theme) => theme.palette.primary.main,
  performance: (theme) => theme.palette.info.main,
  time: (theme) => theme.palette.warning.main,
};

/** Convenience: resolve both the icon and the final accent color for a metric. */
export const getStatAccent = (
  theme: Theme,
  title: string,
  unit = ""
): { Icon: SvgIconComponent; accent: string } => {
  const { Icon, category } = getStatVisual(title, unit);
  return { Icon, accent: CATEGORY_COLOR[category](theme) };
};
